import React, { FC } from 'react';
import { Box, RadioGroup, FormControlLabel, Radio, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import BrazilianBankSearch from '../../../PaymentsPage/components/BrazilianBankSearch';
import { EBeneficiaryType } from '.';

interface AccountOrPixFieldsProps {
  selectedType: EBeneficiaryType;
  setSelectedType: (type: EBeneficiaryType) => void;
  values: any;
  touched: any;
  errors: any;
  readOnly?: boolean;
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
  setFieldValue: (field: string, value: any) => void;
  getErrorText: (name: string) => string | undefined;
}

type TTypeSelectProps = {
  selectedType: EBeneficiaryType;
  setSelectedType: (type: EBeneficiaryType) => void;
  readOnly?: boolean;
};

const TypeSelect: FC<TTypeSelectProps> = ({ selectedType, setSelectedType, readOnly }) => (
  <RadioGroup
    value={selectedType}
    onChange={(_, value) => setSelectedType(value as EBeneficiaryType)}
    sx={{ mb: 2, '& .MuiFormControlLabel-label': { fontSize: 14 } }}
  >
    <Box
      sx={{
        py: 1,
        px: 2,
        borderRadius: '16px',
        backgroundColor: '#F7F9FC',
        mb: 1,
        transition: 'box-shadow 0.2s, outline 0.2s',
      }}
    >
      <FormControlLabel value={EBeneficiaryType.ACCOUNT} control={<Radio size="small" />} label="Dados da conta" />

      <FormControlLabel value={EBeneficiaryType.PIX_KEY} control={<Radio size="small" />} label="Chave PIX" disabled={readOnly} />
    </Box>
  </RadioGroup>
);

const AccountOrPixFields: React.FC<AccountOrPixFieldsProps> = ({
  selectedType,
  setSelectedType,
  values,
  touched,
  errors,
  readOnly,
  handleChange,
  handleBlur,
  setFieldValue,
  getErrorText,
}) => {
  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    setFieldValue(name, value);
  };

  return (
    <>
      <TypeSelect selectedType={selectedType} setSelectedType={setSelectedType} readOnly={readOnly} />

      {selectedType === EBeneficiaryType.PIX_KEY ? (
        <Box mb={2}>
          <TextField
            label="Chave pix"
            fullWidth
            variant="standard"
            placeholder="CPF, CNPJ, e-mail, telefone ou chave aleatória"
            required
            sx={{ mb: 3 }}
            name="pixKey"
            value={values.pixKey}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(touched.pixKey && errors.pixKey)}
            helperText={touched.pixKey && errors.pixKey}
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: '16px', fontWeight: 500 },
            }}
            InputProps={{ sx: { fontSize: '14px' } }}
          />
        </Box>
      ) : (
        <Box mb={2}>
          <BrazilianBankSearch
            value={values.bankCode}
            onChange={(_, code) => {
              setFieldValue('bankCode', code || '');
            }}
            error={Boolean(touched.bankCode && typeof errors.bankCode === 'string')}
            helperText={getErrorText('bankCode')}
          />
          <Box mb={2}>
            <FormControl fullWidth required variant="standard">
              <InputLabel shrink sx={{ fontSize: '16px', fontWeight: 500 }}>
                Tipo de conta
              </InputLabel>
              <Select
                label="Tipo de conta"
                name="accountTypePt"
                value={values.accountTypePt}
                onChange={handleSelectChange}
                onBlur={handleBlur}
                error={Boolean(touched.accountTypePt && errors.accountTypePt)}
                sx={{ fontSize: '14px' }}
                inputProps={{ sx: { fontSize: '14px' } }}
              >
                <MenuItem value="CORRENTE">Corrente</MenuItem>
                <MenuItem value="POUPANCA">Poupança</MenuItem>
                <MenuItem value="PAGAMENTO">Pagamento</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <TextField
              label="Agência *"
              placeholder="0000"
              variant="standard"
              fullWidth
              name="bankBranch"
              type="text"
              value={values.bankBranch}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(touched.bankBranch && errors.bankBranch)}
              helperText={touched.bankBranch && errors.bankBranch}
              InputLabelProps={{
                shrink: true,
                sx: { fontSize: '16px', fontWeight: 500 },
              }}
              InputProps={{ sx: { fontSize: '14px' } }}
            />
            <TextField
              label="Conta *"
              placeholder="000000-0"
              variant="standard"
              type="text"
              fullWidth
              name="bankAccount"
              value={values.bankAccount}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '');

                if (digits.length > 7) {
                  return;
                }

                let formatted = digits;

                if (digits.length <= 6) {
                  formatted = digits;
                } else {
                  formatted = `${digits.slice(0, -1)}-${digits.slice(-1)}`;
                }

                setFieldValue('bankAccount', formatted);
              }}
              onBlur={handleBlur}
              error={Boolean(touched.bankAccount && errors.bankAccount)}
              helperText={touched.bankAccount && errors.bankAccount}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', sx: { fontSize: '14px' } }}
              InputLabelProps={{
                shrink: true,
                sx: { fontSize: '16px', fontWeight: 500 },
              }}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default AccountOrPixFields;
